import React, { useEffect, useMemo, useState } from "react";
import { BadgeDollarSign } from "lucide-react";

// Staff checkout for a specific clinic. /stripe/checkout requires the
// clinic's billing email and name (they ride along as Checkout metadata so
// the webhook can activate and onboard the right clinic), so a client or
// lead must be chosen first. The result is a shareable Stripe Checkout
// link for the clinic to pay - staff never pay it themselves.
export default function BillingCheckoutLinkPanel({ api, showNotice }) {
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selection, setSelection] = useState("");
  const [form, setForm] = useState({ client_email: "", clinic_name: "" });
  const [creating, setCreating] = useState(null);
  const [link, setLink] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([api.get("/clients"), api.get("/leads")]).then(([c, l]) => {
      if (cancelled) return;
      if (c.status === "fulfilled" && Array.isArray(c.value.data)) setClients(c.value.data);
      if (l.status === "fulfilled" && Array.isArray(l.value.data)) setLeads(l.value.data.filter((lead) => lead.status !== "Lost"));
    });
    return () => { cancelled = true; };
  }, [api]);

  const options = useMemo(() => [
    ...clients.map((c) => ({ key: `client:${c.id}`, label: `Client - ${c.clinic_name || c.email}`, client_id: c.id, email: c.email, clinic_name: c.clinic_name })),
    ...leads.filter((l) => !l.client_id).map((l) => ({ key: `lead:${l.id}`, label: `Lead - ${l.clinic_name || l.email}`, lead_id: l.id, email: l.email, clinic_name: l.clinic_name })),
  ], [clients, leads]);

  const selected = options.find((o) => o.key === selection);

  const choose = (key) => {
    setSelection(key);
    setLink(null);
    const option = options.find((o) => o.key === key);
    setForm({ client_email: option?.email || "", clinic_name: option?.clinic_name || "" });
  };

  const create = async (packageId) => {
    if (!form.client_email.trim() || !form.clinic_name.trim()) {
      showNotice("Choose a clinic and make sure it has a billing email and name.", "warning");
      return;
    }
    setCreating(packageId);
    setLink(null);
    try {
      const res = await api.post("/stripe/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
        client_email: form.client_email.trim(),
        clinic_name: form.clinic_name.trim(),
        client_id: selected?.client_id,
        lead_id: selected?.lead_id,
        metadata: { flow: "billing_center" },
      });
      setLink({ url: res.data.url, packageId, reused: res.data.reused });
    } catch (err) {
      const detail = err.response?.data?.detail;
      showNotice(typeof detail === "string" ? detail : "Failed to create checkout", "error");
    }
    setCreating(null);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      showNotice("Checkout link copied", "success");
    } catch {
      showNotice("Copy failed - select the link and copy it manually", "warning");
    }
  };

  return (
    <div className="panel glass-panel" data-testid="billing-checkout-link-panel">
      <div className="panel-header">
        <h3>Checkout Link for a Clinic</h3>
      </div>
      <div className="form-grid">
        <label>
          Clinic
          <select data-testid="billing-checkout-clinic-select" value={selection} onChange={(e) => choose(e.target.value)}>
            <option value="">Choose a client or lead...</option>
            {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </label>
        <label>
          Billing email
          <input data-testid="billing-checkout-email-input" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
        </label>
        <label>
          Clinic name
          <input data-testid="billing-checkout-clinic-name-input" value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} />
        </label>
        <div>
          <button className="primary-btn" data-testid="billing-create-setup-checkout-button" onClick={() => create("setup_fee")} disabled={Boolean(creating)}>
            <BadgeDollarSign size={16} /> {creating === "setup_fee" ? "Creating..." : "Setup Fee ($12,500) link"}
          </button>
        </div>
        <div>
          <button className="primary-btn" data-testid="billing-create-monthly-checkout-button" onClick={() => create("monthly_management")} disabled={Boolean(creating)}>
            <BadgeDollarSign size={16} /> {creating === "monthly_management" ? "Creating..." : "Monthly ($800/month) link"}
          </button>
        </div>
      </div>
      {link && (
        <div data-testid="billing-checkout-link-result">
          <label>
            Send this secure Stripe Checkout link to the clinic{link.reused ? " (reusing the open checkout from the last 30 minutes)" : ""}
            <input readOnly value={link.url} onFocus={(e) => e.target.select()} data-testid="billing-checkout-link-input" />
          </label>
          <div className="button-row">
            <button className="ghost-btn small" onClick={copy} data-testid="billing-checkout-link-copy">Copy link</button>
            <a className="ghost-btn small" href={link.url} target="_blank" rel="noreferrer">Open</a>
          </div>
        </div>
      )}
    </div>
  );
}
